import React from 'react';
import { flowRight as compose, toNumber } from 'lodash';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import ImageSliderStandard from 'components/consumer/image-slider-standard';
import getBreakpoint from 'commons/getBreakpoint';
import ProductPageSectionTitle from 'components/consumer/ProductPageSectionTitle/ProductPageSectionTitle';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import CtaLink from 'components/essentials/Cta/CtaLink';
import withApolloSuspenseQuery from 'components/hoc/withApolloSuspenseQuery';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
import DesktopOnly from 'components/consumer/DesktopOnly';
import ReviewCard from './ReviewCard';
import testimonials from './testimonials';
import { GET_PRODUCT_REVIEWS } from './queries';

const SlickButtonFix = ({ currentSlide, slideCount, children, ...props }) => (
  <span {...props}>{children}</span>
);

SlickButtonFix.propTypes = {
  children: PropTypes.node,
  currentSlide: PropTypes.shape({
    src: PropTypes.string,
    url: PropTypes.string,
    value: PropTypes.string,
  }),
  slideCount: PropTypes.number,
};

const Testimonial = item => (
  <div className="text-base text-gray mr-10 max-w-[70%] min-w-[240px] [&_p]:font-normal">
    <div className="h-10 flex items-center justify-start mx-auto my-0 lg:justify-center">
      <img src={item.image} alt="" />
    </div>
    <p>{item.text}</p>
  </div>
);

class CustomerReviews extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      breakpoint: getBreakpoint(),
      reviews: null,
    };

    this.updateBreakPoint = this.updateBreakPoint.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.data && props.data.productReviews && !state.reviews) {
      return {
        reviews: props.data.productReviews.reviews,
      };
    }
    return null;
  }

  componentDidMount() {
    if (
      this.props.data &&
      this.props.data.productReviews &&
      !this.state.reviews
    ) {
      this.setState({
        reviews: this.props.data.productReviews.reviews,
      });
    }
    window.addEventListener('resize', this.updateBreakPoint, false);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateBreakPoint, false);
  }

  updateBreakPoint() {
    this.setState({
      breakpoint: getBreakpoint(),
    });
  }

  render() {
    const { loading } = this.props.data;
    if (!this.props.data) return null;
    if (loading && !this.props.data.productReviews)
      return (
        <div className="text-lg font-bold flex items-center justify-center">
          Loading..
        </div>
      );
    const { productReviews } = this.props.data;
    if (!this.state.reviews || this.state.reviews.length < 1) {
      // if no reviews then dont show the component.
      return null;
    }

    const { breakpoint } = this.state;
    const { fromModularLink = '' } = this.props;
    let itemsPerRow = 1;
    const averageScore = productReviews.averageRating
      ? productReviews.averageRating
      : 0;

    if (breakpoint === 'tablet') {
      itemsPerRow = 3;
    }
    if (breakpoint === 'desktop') {
      itemsPerRow = 4;
    }

    const sliderSettings = {
      dots: false,
      infinite: this.state.reviews.length > 3,
      speed: 500,
      variableWidth: true,
      slidesToShow: itemsPerRow,
      slidesToScroll: 1,
      rows: 1,
      prevArrow: (
        <SlickButtonFix>
          <IconArrowLeftWhite />
        </SlickButtonFix>
      ),
      nextArrow: (
        <SlickButtonFix>
          <IconArrowRightWhite />
        </SlickButtonFix>
      ),
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            arrows: false,
            adaptiveHeight: true,
            // variableWidth: true,
          },
        },
      ],
    };

    if (this.state.reviews.length <= 4) {
      sliderSettings.slidesToShow = this.state.reviews.length;
    }

    const reviewsList =
      this.state.reviews && this.state.reviews.length
        ? this.state.reviews.map(item => (
            <ReviewCard
              productReview={item}
              key={item.id}
              staticContext={this.props.staticContext}
            />
          ))
        : null;

    const finalCTALink = this.props.product
      ? fromModularLink ||
        `/${this.props.match.params.category}/${this.props.product?.slug}/`
      : '';

    return (
      <div data-xc="customer-product-reviews" className="reviews">
        <div className="p-0 mb-0">
          <MobileAndTabletOnly>
            <div className="lg:hidden text-base pb-2 font-bold text-gray">
              <span>
                {toNumber(averageScore).toFixed(1)} / 5 (
                {productReviews.totalReviews ? productReviews.totalReviews : 0}{' '}
                Reviews)
              </span>
            </div>
          </MobileAndTabletOnly>

          <ProductPageSectionTitle className="text-2xl flex items-center font-bold [&_span]:text-base [&_span]:text-gray [&_span]:mt-[.4rem] [&_span]:ml-4">
            Customers love the{' '}
            {this.props.product
              ? this.props.product.name
              : this.props.productName || ''}
            <DesktopOnly>
              <span className="max-[1023px]:hidden">
                {toNumber(averageScore).toFixed(1)} / 5 (
                {productReviews.totalReviews ? productReviews.totalReviews : 0}{' '}
                Reviews)
              </span>
            </DesktopOnly>
          </ProductPageSectionTitle>
          {this.props.product && this.props.product.name && (
            <p className="text-base mt-0 mx-0 mb-4 hidden lg:block">
              Read real customer reviews and what they’re saying about this
              product.
            </p>
          )}
        </div>
        <div className="pb-8 lg:pt-[.8rem] lg:px-0 lg:pb-8">
          <div className="max-w-full flex-[1_1] block min-h-0 min-w-0 mt-4 mx-0 mb-8 lg:mt-0 lg:mx-0 lg:mb-4 lg:p-0">
            {reviewsList && (
              <ImageSliderStandard
                config={{
                  children: [...reviewsList],
                  sliderConfig: {
                    navType: 'Button',
                    visibleSlides: 3,
                    imageHeight: 430,
                  },
                  variation: { value: 'Component' },
                }}
                fullWidth
                variant="reviews-list"
              />
            )}
          </div>
          {this.props.product?.slug && (
            <CtaLink
              to={`${finalCTALink}reviews`}
              styling="outline-button"
              color="brand"
            >
              See all{' '}
              {productReviews.totalReviews ? productReviews.totalReviews : 0}{' '}
              Reviews
            </CtaLink>
          )}
        </div>

        <div className="flex [border-top:2px_solid_#f8f8f8] overflow-auto pt-8 flex-nowrap pb-0 lg:mt-4 lg:pt-16 lg:pb-0 [&_div]:text-sm [&_div]:flex-[1_1] [&_div]:text-gray [&_div]:mr-8 [&_div]:w-[280px] [&_div:last-child]:last:mr-0 [&_div]:lg:text-center [&_div]:lg:w-auto [&_div]:lg:mr-16 [&_div_p]:font-normal [&_div_p]:mb-0 [&_img]:mb-4">
          {testimonials.map(item => (
            <Testimonial {...item} key={item.image} />
          ))}
        </div>
      </div>
    );
  }
}

CustomerReviews.propTypes = {
  data: PropTypes.shape({
    loading: PropTypes.bool,
    productReviews: PropTypes.shape({
      averageRating: PropTypes.number,
      totalReviews: PropTypes.number,
      reviews: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          rating: PropTypes.number,
          review: PropTypes.string,
          createdAt: PropTypes.string,
          user: PropTypes.shape({
            name: PropTypes.string,
            image: PropTypes.string,
          }),
        })
      ),
    }),
  }),
  fromModularLink: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      category: PropTypes.string,
    }),
  }),
  product: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  productName: PropTypes.string,
  staticContext: PropTypes.object,
};

export default compose(
  withRouter,
  withApolloSuspenseQuery(GET_PRODUCT_REVIEWS, {
    options: ownProps => ({
      variables: {
        id: ownProps.id || null,
        slug: ownProps.slug,
        sortBy: 'date-desc',
        offset: 0,
        limit: 12,
      },
      fetchPolicy: 'cache-and-network',
    }),
  })
)(CustomerReviews);
